import React, { useCallback } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import TesterDetail from './TesterDetail';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import Button from '../Button/Button';
import useMode from '../../hooks/useMode';
import { PortalWithState } from 'react-portal';
import TesterEditModal from './TesterEditModal';
import useNotify from '../../hooks/useNotify';
import { Tester } from './types';
import { ApolloError } from '@apollo/client/errors';

export const GET_COMPANY_TESTER_DETAIL = gql`
  query GetTesterDetail($where: JSON) {
    vendors(where: $where) {
      id
      accountNumber
      status
      profile {
        id
        type
        name
        firstName
        lastName
        phones {
          id
          subType
          value
          extension
        }
        emails {
          id
          subType
          value
        }
        contactAddressId
        contactAddress {
          id
          street1
          street2
          city
          state
          zip
          zipExt
        }
        billingAddressId
        billingAddress {
          id
          street1
          street2
          city
          state
          zip
          zipExt
        }
        backflowTester {
          gauges(where: { archived: null }) {
            id
            gaugeNumber
            calibrationDate
            isDefault
            image {
              contentType
              size
              thumb(size: 350)
              url
            }
          }
        }
        credentials(where: { type: "BACKFLOW", archived: null }) {
          id
          type
          issuer
          value
          city
          state
          issueDate
          expireDate
          isDefault
          created
          updated
          image {
            contentType
            size
            thumb(size: 350)
            url
          }
        }
        perms
      }
      user {
        email
      }
      notes
    }
  }
`;

export const GET_WATER_SUPPLIER_TESTER_DETAIL = gql`
  query GetTesterDetail($where: JSON) {
    vendors(where: $where) {
      id
      accountNumber
      status
      profile {
        id
        type
        name
        phones {
          id
          subType
          value
        }
        emails {
          id
          value
        }
        contactAddressId
        contactAddress {
          street1
          street2
          city
          state
          zip
          zipExt
        }
        billingAddressId
        billingAddress {
          street1
          street2
          city
          state
          zip
          zipExt
        }
        credentials(where: { type: "BUSINESS_LICENSE", archived: null }) {
          id
          type
          issuer
          value
          city
          state
          issueDate
          expireDate
          isDefault
          created
          updated
          image {
            contentType
            size
            thumb(size: 350)
            url
          }
        }
        perms
      }
      notes
      vendors(where: { status: 1 }) {
        status
        id
        profile(where: { type: 1 }, required: true) {
          id
          type
          approval
          firstName
          lastName
          backflowTester(required: true) {
            gauges(where: { archived: null }) {
              id
              gaugeNumber
              calibrationDate
              isDefault
              image {
                contentType
                size
                thumb(size: 350)
                url
              }
            }
          }
          credentials(where: { type: "BACKFLOW", archived: null }) {
            id
            type
            issuer
            value
            city
            state
            issueDate
            expireDate
            isDefault
            created
            updated
            image {
              contentType
              size
              thumb(size: 350)
              url
            }
          }
        }
      }
    }
  }
`;

const SET_VENDOR_STATUS = gql`
  mutation SetVendorStatus($vendorId: ID!, $status: ContractStatus!) {
    setVendorStatus(vendorId: $vendorId, status: $status) {
      id
      status
    }
  }
`;

type TesterThirdPaneProps = {
  vendorId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
};

export default function TesterThirdPane({
  vendorId,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: TesterThirdPaneProps): JSX.Element {
  const mode = useMode();

  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    vendors: Tester[];
  }>(
    mode === 'water-supplier'
      ? GET_WATER_SUPPLIER_TESTER_DETAIL
      : GET_COMPANY_TESTER_DETAIL,
    {
      skip: !vendorId,
      variables: { where: { id: vendorId || null } },
      onError: useCallback(
        (error: ApolloError) => {
          showError(`Error loading vendor ${vendorId}`, error);
        },
        [vendorId]
      ),
    }
  );

  const { showError } = useNotify();

  const vendor = itemData?.vendors?.[0];

  const [setVendorStatus] = useMutation(SET_VENDOR_STATUS, {
    onError: useCallback((error: ApolloError) => {
      showError('Error setting vendor status', error);
    }, []),
  });

  const activateDeactivate = () => {
    if (!vendor) {
      return;
    }
    vendor.status === 'ACTIVE'
      ? setVendorStatus({
          variables: { vendorId: vendor.id, status: 'INACTIVE' },
        })
      : setVendorStatus({
          variables: { vendorId: vendor.id, status: 'ACTIVE' },
        });
  };

  return (
    <ThirdPane visible={!!vendorId}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={vendor ? vendor.profile.name : 'Loading...'}
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      >
        {vendor && (
          <Button
            mr={3}
            variant={vendor.status === 'ACTIVE' ? 'danger' : 'outline'}
            onClick={activateDeactivate}
          >
            {vendor.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </Button>
        )}
        {mode === 'company' && vendor && (
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  mr={10}
                  icon="edit"
                  variant="outline"
                  onClick={openPortal}
                >
                  Edit
                </Button>
                {portal(
                  <TesterEditModal
                    action="edit"
                    perms={vendor.profile.perms}
                    onClose={() => {
                      closePortal();
                      refetch();
                    }}
                    tester={vendor}
                  />
                )}
              </>
            )}
          </PortalWithState>
        )}
      </ThirdPaneHeader>
      <ThirdPaneContent>
        {vendor && <TesterDetail testerDetail={vendor} refetch={refetch} />}
      </ThirdPaneContent>
    </ThirdPane>
  );
}
